import { useRef } from 'react';
import { Box, Chip, IconButton, LinearProgress, Tooltip, Typography } from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import type { PendingAttachment } from '../hooks/useMessageAttachments';

interface MessageComposerAttachmentsProps {
  attachments: PendingAttachment[];
  onAddFiles: (files: File[]) => void;
  onRemove: (id: string) => void;
  disabled?: boolean;
}

function getFileIcon(contentType: string) {
  if (contentType.startsWith('image/')) return <ImageIcon fontSize="small" />;
  if (contentType === 'application/pdf') return <PictureAsPdfIcon fontSize="small" />;
  return <InsertDriveFileIcon fontSize="small" />;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function MessageComposerAttachments({
  attachments,
  onAddFiles,
  onRemove,
  disabled = false,
}: MessageComposerAttachmentsProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      onAddFiles(Array.from(files));
    }
    // Reset input so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleAttachClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <Box sx={{ mt: 1 }}>
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        onChange={handleFileSelect}
        style={{ display: 'none' }}
        disabled={disabled}
      />

      {/* Attach button and attachment chips */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Tooltip title="Attach files">
          <IconButton
            size="small"
            onClick={handleAttachClick}
            disabled={disabled}
            sx={{ color: 'text.secondary' }}
          >
            <AttachFileIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        {attachments.map(attachment => (
          <Chip
            key={attachment.id}
            icon={getFileIcon(attachment.file.type)}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="caption" noWrap sx={{ maxWidth: 120 }}>
                  {attachment.file.name}
                </Typography>
                {attachment.status === 'complete' && (
                  <Typography variant="caption" color="text.secondary">
                    ({formatFileSize(attachment.file.size)})
                  </Typography>
                )}
                {attachment.status === 'uploading' && (
                  <LinearProgress
                    sx={{ width: 40, height: 4, ml: 0.5 }}
                    variant="indeterminate"
                  />
                )}
              </Box>
            }
            onDelete={disabled ? undefined : () => onRemove(attachment.id)}
            deleteIcon={<CloseIcon fontSize="small" />}
            size="small"
            color={attachment.status === 'error' ? 'error' : 'default'}
            variant="outlined"
            sx={{
              '& .MuiChip-label': { display: 'flex', alignItems: 'center' },
            }}
          />
        ))}
      </Box>

      {/* Error messages */}
      {attachments.some(a => a.status === 'error') && (
        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
          Some files failed to upload. Click X to remove and try again.
        </Typography>
      )}
    </Box>
  );
}
